// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logomark } from '../ui/Logomark.js';
import { useApp } from '../../store/AppContext.js';
import { loadSession } from '../../lib/session.js';
import { getBiometryInfo, runBiometricPrompt } from '../../native/biometric.js';
import { hapticSuccess } from '../../native/haptics.js';

// Native-only unlock screen. A valid session sits in the Keychain/Keystore; the
// user proves presence via biometrics to lift the key into memory. The password
// path is always one tap away as the fallback.
export function BiometricLockScreen() {
  const { t } = useTranslation(['auth', 'common']);
  const { state, dispatch } = useApp();
  const [biometryName, setBiometryName] = useState(() => t('biometric.defaultMethod'));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const attemptedRef = useRef(false);

  const attempt = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    const ok = await runBiometricPrompt(t('biometric.unlockPrompt'));
    if (!ok) {
      setFailed(true);
      setBusy(false);
      return;
    }
    // Biometry succeeded — now materialize the persisted session.
    const result = await loadSession();
    if (result.session) {
      await hapticSuccess();
      dispatch({ type: 'SESSION_RESTORED', session: result.session });
    } else {
      // Session vanished/expired between lock and unlock — fall back to login.
      dispatch({ type: 'BOOTSTRAP_DONE', expired: result.expired });
    }
  }, [busy, dispatch, t]);

  useEffect(() => {
    void getBiometryInfo().then((info) => setBiometryName(info.friendlyName));
    // Auto-present the prompt once on mount — the common case is "open app, look
    // at phone, you're in".
    if (!attemptedRef.current) {
      attemptedRef.current = true;
      void attempt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function usePassword() {
    dispatch({ type: 'BOOTSTRAP_DONE', expired: false });
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-6 pb-safe pt-safe">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center justify-center gap-2 text-2xl font-semibold text-text mb-1">
          <Logomark size={24} />
          {t('common:appName')}
        </div>
        <p className="text-sm text-text-muted mb-10">
          {state.auth.username
            ? t('biometric.welcomeBackNamed', { username: state.auth.username })
            : t('biometric.welcomeBack')}
        </p>

        <button
          onClick={() => void attempt()}
          disabled={busy}
          className="w-full bg-accent text-white py-3 text-md font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
        >
          {busy ? t('biometric.waiting') : t('biometric.unlockWith', { method: biometryName })}
        </button>

        {failed && (
          <p className="text-sm text-negative mt-4">
            {t('biometric.notRecognized', { method: biometryName })}
          </p>
        )}

        <button
          onClick={usePassword}
          className="mt-6 text-sm text-text-muted hover:text-text transition-colors"
        >
          {t('biometric.usePassword')}
        </button>
      </div>
    </div>
  );
}
